// MobileAnalytics.js - Mobile-specific analytics and performance tracking
// Tracks mobile usage patterns, performance metrics, and user engagement

export default class MobileAnalytics {
  constructor(offlineManager) {
    this.offlineManager = offlineManager;
    this.sessionStartTime = Date.now();
    this.touchInteractions = 0;
    this.gestureEvents = [];
    this.performanceMetrics = {};
    this.deviceInfo = this.getDeviceInfo();
    this.isTracking = true;
    
    this.init();
  }

  init() {
    console.log('📊 MobileAnalytics: Initializing mobile analytics...');
    
    // Track app launch
    this.trackEvent('app_launch', {
      timestamp: this.sessionStartTime,
      deviceInfo: this.deviceInfo,
      isStandalone: window.navigator.standalone || window.matchMedia('(display-mode: standalone)').matches
    });
    
    // Set up performance monitoring
    this.startPerformanceMonitoring();
    
    // Set up interaction tracking
    this.setupInteractionTracking();
    
    // Set up visibility change tracking
    this.setupVisibilityTracking();
    
    // Track session end on page unload
    window.addEventListener('beforeunload', () => {
      this.trackSessionEnd();
    });
    
    console.log('✅ MobileAnalytics: Mobile analytics initialized');
  }

  getDeviceInfo() {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    
    return {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      cookieEnabled: navigator.cookieEnabled,
      onLine: navigator.onLine,
      screenWidth: screen.width,
      screenHeight: screen.height,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      devicePixelRatio: window.devicePixelRatio,
      colorDepth: screen.colorDepth,
      pixelDepth: screen.pixelDepth,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      webglRenderer: gl ? gl.getParameter(gl.RENDERER) : 'Not available',
      webglVendor: gl ? gl.getParameter(gl.VENDOR) : 'Not available',
      touchSupport: 'ontouchstart' in window,
      maxTouchPoints: navigator.maxTouchPoints || 0,
      orientation: screen.orientation ? screen.orientation.angle : window.orientation,
      connectionType: navigator.connection ? navigator.connection.effectiveType : 'unknown',
      memory: navigator.deviceMemory || 'unknown',
      hardwareConcurrency: navigator.hardwareConcurrency || 'unknown'
    };
  }

  startPerformanceMonitoring() {
    // Monitor frame rate
    let frameCount = 0;
    let lastTime = performance.now();
    
    const measureFPS = () => {
      frameCount++;
      const currentTime = performance.now();
      
      if (currentTime - lastTime >= 1000) {
        const fps = Math.round((frameCount * 1000) / (currentTime - lastTime));
        this.performanceMetrics.fps = fps;
        
        if (fps < 30) {
          this.trackEvent('performance_low_fps', { fps, timestamp: currentTime });
        }
        
        frameCount = 0;
        lastTime = currentTime;
      }
      
      requestAnimationFrame(measureFPS);
    };
    
    requestAnimationFrame(measureFPS);
    
    // Monitor memory usage (if available)
    if (performance.memory) {
      setInterval(() => {
        this.performanceMetrics.memory = {
          usedJSHeapSize: performance.memory.usedJSHeapSize,
          totalJSHeapSize: performance.memory.totalJSHeapSize,
          jsHeapSizeLimit: performance.memory.jsHeapSizeLimit
        };
        
        // Track memory warnings
        const memoryUsagePercent = (performance.memory.usedJSHeapSize / performance.memory.jsHeapSizeLimit) * 100;
        if (memoryUsagePercent > 80) {
          this.trackEvent('performance_high_memory', {
            memoryUsagePercent,
            memoryInfo: this.performanceMetrics.memory
          });
        }
      }, 10000); // Check every 10 seconds
    }
    
    // Monitor page load performance
    window.addEventListener('load', () => {
      setTimeout(() => {
        const perfData = performance.getEntriesByType('navigation')[0];
        if (perfData) {
          this.trackEvent('page_load_performance', {
            loadTime: perfData.loadEventEnd - perfData.navigationStart,
            domContentLoaded: perfData.domContentLoadedEventEnd - perfData.navigationStart,
            firstPaint: this.getFirstPaint(),
            firstContentfulPaint: this.getFirstContentfulPaint()
          });
        }
      }, 0);
    });
  }

  getFirstPaint() {
    const paintEntries = performance.getEntriesByType('paint');
    const firstPaint = paintEntries.find(entry => entry.name === 'first-paint');
    return firstPaint ? firstPaint.startTime : null;
  }

  getFirstContentfulPaint() {
    const paintEntries = performance.getEntriesByType('paint');
    const firstContentfulPaint = paintEntries.find(entry => entry.name === 'first-contentful-paint');
    return firstContentfulPaint ? firstContentfulPaint.startTime : null;
  }

  setupInteractionTracking() {
    // Track touch interactions
    document.addEventListener('touchstart', (e) => {
      this.touchInteractions++;
      this.trackEvent('touch_interaction', {
        touchCount: e.touches.length,
        target: e.target.tagName,
        timestamp: Date.now()
      });
    }, { passive: true });
    
    // UNIFIED: Gesture tracking now handled by UnifiedMobileSystem
    // Gesture events will be tracked through the unified event system
    console.log('📊 Gesture tracking delegated to UnifiedMobileSystem');
    
    // Track orientation changes
    window.addEventListener('orientationchange', () => {
      setTimeout(() => {
        this.trackEvent('orientation_change', {
          orientation: window.orientation === 0 ? 'portrait' : 'landscape',
          width: window.innerWidth,
          height: window.innerHeight
        });
      }, 100);
    });
  }

  setupVisibilityTracking() {
    let visibilityStartTime = Date.now();
    
    document.addEventListener('visibilitychange', () => {
      const now = Date.now();
      
      if (document.hidden) {
        // App went to background
        const visibleDuration = now - visibilityStartTime;
        this.trackEvent('app_background', {
          visibleDuration,
          timestamp: now
        });
      } else {
        // App came to foreground
        visibilityStartTime = now;
        this.trackEvent('app_foreground', {
          timestamp: now
        });
      }
    });
  }

  trackGesture(gestureType, data) {
    const gestureEvent = {
      type: gestureType,
      ...data,
      timestamp: Date.now()
    };
    
    this.gestureEvents.push(gestureEvent);
    
    // Keep only last 100 gesture events
    if (this.gestureEvents.length > 100) {
      this.gestureEvents = this.gestureEvents.slice(-100);
    }
    
    this.trackEvent('mobile_gesture', gestureEvent);
  }

  trackEvent(eventType, data = {}) {
    if (!this.isTracking) return;
    
    const event = {
      type: eventType,
      data: {
        ...data,
        sessionId: this.getSessionId(),
        sessionDuration: Date.now() - this.sessionStartTime,
        touchInteractions: this.touchInteractions,
        performanceMetrics: this.performanceMetrics
      },
      timestamp: Date.now(),
      deviceInfo: this.deviceInfo
    };
    
    // Use offline manager to handle analytics
    if (this.offlineManager) {
      this.offlineManager.trackAnalytics(eventType, event);
    } else {
      console.log('📊 Analytics Event:', eventType, event);
    }
  }

  // Game-specific tracking methods
  trackGameStart(gameType) {
    this.trackEvent('game_start', {
      gameType,
      deviceOrientation: window.orientation === 0 ? 'portrait' : 'landscape'
    });
  }

  trackGameEnd(gameType, duration, score, performance) {
    this.trackEvent('game_end', {
      gameType,
      duration,
      score,
      performance,
      gestureCount: this.gestureEvents.length,
      averageFPS: this.performanceMetrics.fps
    });
  }

  trackPhilosophyFlowSession(sessionData) {
    this.trackEvent('philosophy_flow_session', {
      ...sessionData,
      mobileOptimized: true,
      touchInteractions: this.touchInteractions,
      sessionGestures: this.gestureEvents.slice(-20) // Last 20 gestures
    });
  }

  trackSceneTransition(fromScene, toScene, transitionTime) {
    this.trackEvent('scene_transition', {
      fromScene,
      toScene,
      transitionTime,
      isMobile: true
    });
  }

  trackPWAInstall() {
    this.trackEvent('pwa_install', {
      installMethod: 'mobile',
      deviceInfo: this.deviceInfo
    });
  }

  trackOfflineUsage(feature) {
    this.trackEvent('offline_usage', {
      feature,
      timestamp: Date.now()
    });
  }

  // Performance tracking methods
  trackLoadTime(resource, loadTime) {
    this.trackEvent('resource_load_time', {
      resource,
      loadTime,
      connectionType: navigator.connection ? navigator.connection.effectiveType : 'unknown'
    });
  }

  trackError(error, context) {
    this.trackEvent('mobile_error', {
      error: error.message,
      stack: error.stack,
      context,
      deviceInfo: this.deviceInfo
    });
  }

  // Session management
  getSessionId() {
    if (!this.sessionId) {
      this.sessionId = `mobile_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }
    return this.sessionId;
  }

  trackSessionEnd() {
    const sessionDuration = Date.now() - this.sessionStartTime;
    
    this.trackEvent('session_end', {
      sessionDuration,
      totalTouchInteractions: this.touchInteractions,
      totalGestures: this.gestureEvents.length,
      averageFPS: this.performanceMetrics.fps,
      finalMemoryUsage: this.performanceMetrics.memory
    });
  }

  // Public API methods
  enable() {
    this.isTracking = true;
    console.log('✅ Mobile analytics enabled');
  }

  disable() {
    this.isTracking = false;
    console.log('🚫 Mobile analytics disabled');
  }

  getSessionStats() {
    return {
      sessionId: this.getSessionId(),
      duration: Date.now() - this.sessionStartTime,
      touchInteractions: this.touchInteractions,
      gestureEvents: this.gestureEvents.length,
      performanceMetrics: this.performanceMetrics,
      deviceInfo: this.deviceInfo
    };
  }

  exportAnalyticsData() {
    return {
      sessionStats: this.getSessionStats(),
      recentGestures: this.gestureEvents.slice(-50),
      deviceInfo: this.deviceInfo,
      performanceMetrics: this.performanceMetrics
    };
  }
}
